import React, { useState } from 'react';
import { Search, Eye, Copy, Send, Zap, BellOff, Ban, Pencil } from 'lucide-react';
import useSuperAdminMarketingStore from '@/stores/SuperAdminStore/SuperAdminMarketingStore';
import { NotificationRecord } from '@/services/PagesServices/SuperAdminMarketingService';
import './NotificationHistory.css';

interface NotificationHistoryProps {
  onViewNotification: (notification: NotificationRecord) => void;
  onNewNotification: () => void;
  onEditNotification: (notification: NotificationRecord) => void;
}

// Only drafts and not-yet-sent scheduled campaigns can be edited.
const isEditable = (status: string) => status === 'draft' || status === 'scheduled';

const NotificationHistory: React.FC<NotificationHistoryProps> = ({ onViewNotification, onNewNotification, onEditNotification }) => {
  const { notificationHistory, sending, resendCampaign, duplicateCampaign, cancelCampaign } = useSuperAdminMarketingStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [actionMessage, setActionMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleResend = async (notif: NotificationRecord) => {
    const result = await resendCampaign(notif.uuid || '');
    setActionMessage(result.success
      ? { type: 'success', text: 'Notification resent successfully.' }
      : { type: 'error', text: result.error || 'Failed to resend notification.' });
  };

  const handleDuplicate = async (notif: NotificationRecord) => {
    const result = await duplicateCampaign(notif);
    setActionMessage(result.success
      ? { type: 'success', text: 'Notification duplicated as a new draft.' }
      : { type: 'error', text: result.error || 'Failed to duplicate notification.' });
  };

  const handleCancel = async (notif: NotificationRecord) => {
    if (!window.confirm(`Cancel "${notif.title}"? This removes the notification and stops it from being sent.`)) {
      return;
    }
    const result = await cancelCampaign(notif.uuid || '');
    setActionMessage(result.success
      ? { type: 'success', text: 'Notification cancelled.' }
      : { type: 'error', text: result.error || 'Failed to cancel notification.' });
  };

  const getTypeLabel = (type: string) => {
    if (type === 'email') return 'Email';
    if (type === 'in-app') return 'In-App';
    return 'Both';
  };

  const getStatusColor = (status: string) => {
    if (status === 'sent') return '#10b981';
    if (status === 'scheduled') return '#3b82f6';
    return '#f59e0b';
  };

  const filteredNotifications = notificationHistory?.filter(notif => {
    const matchesSearch = notif.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || notif.type === filterType;
    const matchesStatus = filterStatus === 'all' || notif.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  }) || [];

  return (
    <div className="s-admin-marketing-history-content">
      <div className="s-admin-marketing-history-header">
        <div>
          <h2 className="s-admin-marketing-section-title">Notification History</h2>
          <p className="s-admin-marketing-section-subtitle">View all sent and scheduled notifications</p>
        </div>
        <button className="s-admin-marketing-new-btn" onClick={onNewNotification}>
          <span>+</span>
          New Notification
        </button>
      </div>

      {actionMessage && (
        <div
          style={{
            margin: '0 0 12px', padding: '10px 14px', borderRadius: 8, fontSize: 14,
            background: actionMessage.type === 'success' ? '#ecfdf5' : '#fef2f2',
            color: actionMessage.type === 'success' ? '#065f46' : '#991b1b',
            border: `1px solid ${actionMessage.type === 'success' ? '#a7f3d0' : '#fecaca'}`
          }}
        >
          {actionMessage.text}
        </div>
      )}

      <div className="s-admin-marketing-filters">
        <div className="s-admin-marketing-search-box">
          <Search size={18} className="s-admin-marketing-search-icon" />
          <input
            type="text"
            className="s-admin-marketing-search-input"
            placeholder="Search notifications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <select
          className="s-admin-marketing-filter-select"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Type</option>
          <option value="email">Email</option>
          <option value="in-app">In-App</option>
          <option value="both">Both</option>
        </select>
        <select
          className="s-admin-marketing-filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="sent">Sent</option>
          <option value="scheduled">Scheduled</option>
          <option value="draft">Draft</option>
        </select>
      </div>

      <div className="s-admin-marketing-table-container">
        {filteredNotifications.length === 0 ? (
          <div className="s-admin-marketing-empty-state">
            <BellOff size={56} className="s-admin-marketing-empty-icon" />
            <h3 className="s-admin-marketing-empty-title">No Notifications Found</h3>
            <p className="s-admin-marketing-empty-message">
              {!notificationHistory || notificationHistory.length === 0
                ? "You haven't sent any notifications yet. Create your first notification to get started!"
                : "No notifications match your current filters. Try adjusting your search or filter criteria."}
            </p>
          </div>
        ) : (
          <table className="s-admin-marketing-history-table">
            <thead>
              <tr>
                <th>NOTIFICATION</th>
                <th>TYPE</th>
                <th>AUDIENCE</th>
                <th>STATUS</th>
                <th>DATE</th>
                <th>PERFORMANCE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredNotifications.map(notif => (
                <tr key={notif.id}>
                  <td>
                    <div className="s-admin-marketing-notif-cell">
                      <div className="s-admin-marketing-notif-icon">
                        <Zap size={18} />
                      </div>
                      <div>
                        <div className="s-admin-marketing-notif-title">{notif.title}</div>
                        <div className="s-admin-marketing-notif-subtitle">{notif.subtitle}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="s-admin-marketing-type-badge">{getTypeLabel(notif.type)}</span>
                  </td>
                  <td>
                    <div className="s-admin-marketing-audience-cell">
                      {notif.audiences.map((aud, idx) => (
                        <div key={idx} className="s-admin-marketing-audience-tag">{aud}</div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <span
                      className="s-admin-marketing-status-badge"
                      style={{ backgroundColor: getStatusColor(notif.status) + '20', color: getStatusColor(notif.status) }}
                    >
                      {notif.status.charAt(0).toUpperCase() + notif.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="s-admin-marketing-date-cell">
                      <div>{notif.date || '-'}</div>
                      <div className="s-admin-marketing-date-label">{notif.dateLabel}</div>
                    </div>
                  </td>
                  <td>
                    {notif.openRate ? (
                      <div className="s-admin-marketing-performance-cell">
                        <div className="s-admin-marketing-metric">
                          <Eye size={14} />
                          <span>{notif.openRate}%</span>
                        </div>
                        <div className="s-admin-marketing-metric">
                          <Zap size={14} />
                          <span>{notif.clickRate}%</span>
                        </div>
                      </div>
                    ) : (
                      <span>-</span>
                    )}
                  </td>
                  <td>
                    <div className="s-admin-marketing-actions">
                      <button
                        className="s-admin-marketing-action-btn"
                        onClick={() => onViewNotification(notif)}
                        title="View"
                      >
                        <Eye size={16} />
                      </button>
                      {isEditable(notif.status) && (
                        <button
                          className="s-admin-marketing-action-btn"
                          title="Edit"
                          disabled={sending}
                          onClick={() => onEditNotification(notif)}
                        >
                          <Pencil size={16} />
                        </button>
                      )}
                      <button
                        className="s-admin-marketing-action-btn"
                        title="Duplicate"
                        disabled={sending}
                        onClick={() => handleDuplicate(notif)}
                      >
                        <Copy size={16} />
                      </button>
                      <button
                        className="s-admin-marketing-action-btn"
                        title="Resend"
                        disabled={sending}
                        onClick={() => handleResend(notif)}
                      >
                        <Send size={16} />
                      </button>
                      <button
                        className="s-admin-marketing-action-btn"
                        title="Cancel"
                        disabled={sending}
                        onClick={() => handleCancel(notif)}
                      >
                        <Ban size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default NotificationHistory;
